module.exports = function decorateBenchmark($benchmark = {}, opts = {}) {
	$benchmark.store = {};
	var noop = () => {};

	$benchmark.enabled = opts.enabled !== undefined ? !!opts.enabled : false;
	$benchmark.setEnabled = val => $benchmark.enabled = !!val;

	$benchmark.now = () => typeof performance !== 'undefined' && performance.now
		? performance.now()
		: Date.now();

	$benchmark.t0 = $benchmark.now();

	$benchmark.inferKind = label => /^http\./.test(label)
		? 'http'
		: /^cpu\./.test(label)
			? 'cpu'
			: undefined;

	$benchmark.record = (label, ms, opts = {}) => {
		if (!$benchmark.enabled) return ms;
		var leaf = $benchmark.store[label] || ($benchmark.store[label] = {ms: 0, count: 0});
		leaf.ms += ms;
		leaf.count++;
		var kind = opts.kind || $benchmark.inferKind(label);
		if (kind && !leaf.kind) leaf.kind = kind;
		if (leaf.minMs === undefined || ms < leaf.minMs) leaf.minMs = ms;
		if (leaf.maxMs === undefined || ms > leaf.maxMs) leaf.maxMs = ms;
		var end = $benchmark.now() - $benchmark.t0;
		var start = end - ms;
		if (leaf.startMs === undefined || start < leaf.startMs) leaf.startMs = start;
		if (leaf.endMs === undefined || end > leaf.endMs) leaf.endMs = end;
		return ms;
	};

	$benchmark.start = (label, opts) => {
		if (!$benchmark.enabled) return noop;
		var t = $benchmark.now();
		return () => $benchmark.record(label, $benchmark.now() - t, opts);
	};

	$benchmark.wrap = (label, fn, opts) => {
		if (!$benchmark.enabled) return Promise.resolve().then(fn);
		var done = $benchmark.start(label, opts);
		return Promise.resolve().then(fn).finally(done);
	};

	$benchmark.time = (label, fn, opts) => {
		if (!$benchmark.enabled) return fn();
		var done = $benchmark.start(label, {kind: 'cpu', ...opts});
		try {
			return fn();
		} finally {
			done();
		}
	};

	$benchmark.pending = [];
	$benchmark.track = promise => {
		if (!$benchmark.enabled) return promise;
		$benchmark.pending.push(promise);
		Promise.resolve(promise).catch(() => {}).finally(() => {
			var i = $benchmark.pending.indexOf(promise);
			if (i >= 0) $benchmark.pending.splice(i, 1);
		});
		return promise;
	};

	$benchmark.reset = () => { $benchmark.store = {}; $benchmark.pending = []; $benchmark.t0 = $benchmark.now(); };

	$benchmark.rows = () => Object.keys($benchmark.store)
		.map(k => {
			var leaf = $benchmark.store[k];
			return {
				operation: k,
				kind: leaf.kind || $benchmark.inferKind(k) || 'other',
				totalMs: Math.round(leaf.ms * 10) / 10,
				count: leaf.count,
				avgMs: Math.round((leaf.ms / (leaf.count || 1)) * 100) / 100,
				lowestMs: leaf.minMs === undefined ? null : Math.round(leaf.minMs * 100) / 100,
				highestMs: leaf.maxMs === undefined ? null : Math.round(leaf.maxMs * 100) / 100,
				startMs: leaf.startMs === undefined ? null : Math.round(leaf.startMs),
				endMs: leaf.endMs === undefined ? null : Math.round(leaf.endMs),
			};
		})
		.sort((a, b) => b.totalMs - a.totalMs);

	$benchmark.total = () => Math.round(Object.keys($benchmark.store).reduce((sum, k) => sum + $benchmark.store[k].ms, 0) * 10) / 10;

	$benchmark.rollup = keyOf => {
		var groups = {};
		Object.keys($benchmark.store).forEach(k => {
			var leaf = $benchmark.store[k];
			var key = keyOf(k, leaf);
			var g = groups[key] || (groups[key] = {key, ms: 0, count: 0, startMs: undefined, endMs: undefined});
			g.ms += leaf.ms;
			g.count += leaf.count;
			if (leaf.startMs !== undefined && (g.startMs === undefined || leaf.startMs < g.startMs)) g.startMs = leaf.startMs;
			if (leaf.endMs !== undefined && (g.endMs === undefined || leaf.endMs > g.endMs)) g.endMs = leaf.endMs;
		});
		return Object.keys(groups)
			.map(k => {
				var g = groups[k];
				return {
					group: k,
					totalMs: Math.round(g.ms * 10) / 10,
					spanMs: g.startMs === undefined ? null : Math.round(g.endMs - g.startMs),
					count: g.count,
				};
			})
			.sort((a, b) => b.totalMs - a.totalMs);
	};

	$benchmark.summary = () => $benchmark.rollup((k, leaf) => leaf.kind || $benchmark.inferKind(k) || 'other');

	$benchmark.groups = () => $benchmark.rollup(k => k.includes('.') ? k.slice(0, k.lastIndexOf('.')) : k);

	$benchmark.report = async (opts = {}) => {
		var deadline = $benchmark.now() + (opts.timeout || 8000);
		while ($benchmark.pending.length && $benchmark.now() < deadline) {
			await Promise.race([
				Promise.allSettled($benchmark.pending.slice()),
				new Promise(resolve => setTimeout(resolve, Math.max(0, deadline - $benchmark.now()))),
			]);
			await new Promise(resolve => setTimeout(resolve, 250));
		}
		var rows = $benchmark.rows();
		if (opts.log !== false) {
			var hasStyle = typeof document !== 'undefined';
			var heading = (hasStyle ? '%c' : '') + `⏱ benchmark${opts.label ? ' ' + opts.label : ''} — ${$benchmark.total()}ms total`;
			if (console.groupCollapsed) console.groupCollapsed.apply(console, hasStyle ? [heading, 'color:#009ACD;font-weight:bold'] : [heading]);
			console.table($benchmark.summary());
			console.table($benchmark.groups());
			console.table(rows);
			if (console.groupEnd) console.groupEnd();
		}
		if (opts.persist && typeof $benchmark.persist === 'function') $benchmark.persist(opts.meta);
		return rows;
	};

	return $benchmark;
};

var _global = typeof globalThis !== 'undefined' ? globalThis
	: typeof window !== 'undefined' ? window
	: typeof global !== 'undefined' ? global
	: {};
module.exports.singleton = _global.__aosBenchmark || (_global.__aosBenchmark = module.exports({}, {enabled: false}));
